import { useCallback, useEffect, useRef, useState } from 'react';
import { isIdentityProviderReachable } from './identity-provider-reachability.service';

export const RECHECK_INTERVAL_MS = 10_000;

export type StartupConnectivityStatus = 'checking' | 'online' | 'offline';

interface UseStartupConnectivityOptions {
  enabled: boolean;
}

export const useStartupConnectivity = ({ enabled }: UseStartupConnectivityOptions) => {
  const [status, setStatus] = useState<StartupConnectivityStatus>(enabled ? 'checking' : 'online');
  const [isChecking, setIsChecking] = useState(false);
  const isCheckInFlight = useRef(false);

  const check = useCallback(async () => {
    if (isCheckInFlight.current) {
      return;
    }
    isCheckInFlight.current = true;
    setIsChecking(true);

    const isReachable = await isIdentityProviderReachable();

    isCheckInFlight.current = false;
    setIsChecking(false);
    setStatus(isReachable ? 'online' : 'offline');
  }, []);

  useEffect(() => {
    if (enabled) {
      void check();
    }
  }, [enabled, check]);

  useEffect(() => {
    if (status !== 'offline') {
      return;
    }
    const intervalId = setInterval(() => void check(), RECHECK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [status, check]);

  return { status, isChecking, retry: check };
};
